import { useEffect, useState } from "react";
import { NumberInput } from "../../../components/ui/inputs";
import { Button, Card, CardBody } from "../../../components/ui";
import localization from "../../../config/localization";
import { responsibleGamingService } from "../service/responsibleGaming.service";
import { toastService as toast } from "../../../components/ui/toast/toast.service";
import { localStorageService } from "../../../services";
import { useDepositLimitTimer } from "../hooks/useDepositLimitTimer";
import { RetrieveUserDepositLimitResponse } from "../models/response";
import { DeviceType } from "../../../models/constants/deviceType";
import userDeviceStore from "../../../store/device.store";

export function DepositLimit() {
  const [data, setData] =
    useState<RetrieveUserDepositLimitResponse | null>(null);

  const deviceType = userDeviceStore((state) => state.deviceType);

  const daily = useDepositLimitTimer({
    currentAmount: data?.depositamount,
    newAmount: data?.newdepositamount,
    applicableOn: data?.newdailydepositapplicableon,
    message: (amount, time) =>
      localization.dailydepositlimitnotes
        .replace("{0}", amount.toString())
        .replace("{1}", time),
  });

  const weekly = useDepositLimitTimer({
    currentAmount: data?.weeklydepositamount,
    newAmount: data?.newweeklydepositamount,
    applicableOn: data?.newweeklydepositapplicableon,
    message: (amount, time) =>
      localization.weeklydepositlimitnotes
        .replace("{0}", amount.toString())
        .replace("{1}", time),
  });

  const monthly = useDepositLimitTimer({
    currentAmount: data?.monthlydepositamount,
    newAmount: data?.newmonthlydepositamount,
    applicableOn: data?.newmonthlydepositapplicableon,
    message: (amount, time) =>
      localization.monthlydepositlimitnotes
        .replace("{0}", amount.toString())
        .replace("{1}", time),
  });

  const fetchDepositLimit = () => {
    responsibleGamingService
      .retrieveDepositLimit({
        id: localStorageService.getItem<string>("userId") ?? "",
      })
      .then((response) => {
        if (response.isSuccess) setData(response.data);
      });
  };

  useEffect(() => {
    fetchDepositLimit();
  }, []);

  const notes = [
    { note: daily.note, type: 1, amount: data?.newdepositamount },
    { note: weekly.note, type: 2, amount: data?.newweeklydepositamount },
    { note: monthly.note, type: 3, amount: data?.newmonthlydepositamount },
  ];

  const handleSubmit = () => {
    if (!daily.amount && !weekly.amount && !monthly.amount) {
      toast.error(localization.depositlimitvalidationforblankdata);
      return;
    }

    if (Number(daily.amount) < 0) {
      toast.error(localization.dailydepositlimitnegativeamountvalidation);
      return;
    }

    if (Number(weekly.amount) < 0) {
      toast.error(localization.weeklydepositlimitnegativeamountvalidation);
      return;
    }

    if (Number(monthly.amount) < 0) {
      toast.error(localization.monthlydepositlimitnegativeamountvalidation);
      return;
    }

    if (Number(daily.amount) > Number(weekly.amount)) {
      toast.error(localization.dailylimit_biggerthan_monthlyweekly);
      fetchDepositLimit();
      return;
    }

    if (Number(daily.amount) > Number(monthly.amount)) {
      toast.error(localization.dailylimit_biggerthan_monthlyweekly);
      fetchDepositLimit();
      return;
    }

    if (Number(weekly.amount) > Number(monthly.amount)) {
      toast.error(localization.weeklylimit_biggerthan_weekly);
      fetchDepositLimit();
      return;
    }

    responsibleGamingService
      .persistDepositLimit({
        isDepositLimit: Number(daily.amount) > 0,
        depositAmount: daily.amount ? Number(daily.amount) : null,
        isWeeklyDepositLimit: Number(weekly.amount) > 0,
        weeklyDepositAmount: weekly.amount
          ? Number(weekly.amount)
          : null,
        isMonthlyDepositLimit: Number(monthly.amount) > 0,
        monthlyDepositAmount: monthly.amount
          ? Number(monthly.amount)
          : null,
      })
      .then((response) => {
        if (response.isSuccess) {
          var message = localization.getString(response.message) ?? localization.depositlimitsuccessmessage;
          toast.success(message);
          fetchDepositLimit();
        }
      });
  };

  const enableControl = (id: string) => {
    const element = document.getElementById(id) as HTMLInputElement;
    if (element) {
      element.disabled = false;
      element.focus();
    }
  };

  const handleUndoRequest = (type: number, amount: number | null) => {
    responsibleGamingService
      .undoDepositLimit({
        cancelDepositType: type,
        cancelDepositAmount: amount,
      })
      .then((response) => {
        if (response.isSuccess) fetchDepositLimit();
      });
  };

  const content = (
    <div className="space-y-6 px-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">

        <NumberInput
          id="dailyDepositLimit"
          label={`${localization.daily_deposit_limit} (₹)`}
          value={daily.amount}
          placeholder={localization.daily_deposit_limit_placeholder}
          iconSrc={
            data?.depositamount && Number(data.depositamount) > 0
              ? "/icons/ic-edit.svg"
              : undefined
          }
          disabled={daily.disabled}
          onButtonClick={() => enableControl("dailyDepositLimit")}
          onChange={(e) => daily.setAmount(e.target.value)}
        />

        <NumberInput
          id="weeklyDepositLimit"
          label={`${localization.weekly_deposit} (₹)`}
          value={weekly.amount}
          placeholder={localization.weekly_deposit_limit_placeholder}
          iconSrc={
            data?.weeklydepositamount &&
              Number(data.weeklydepositamount) > 0
              ? "/icons/ic-edit.svg"
              : undefined
          }
          disabled={weekly.disabled}
          onButtonClick={() => enableControl("weeklyDepositLimit")}
          onChange={(e) => weekly.setAmount(e.target.value)}
        />

        <NumberInput
          id="monthlyDepositLimit"
          label={`${localization.month_deposit} (₹)`}
          value={monthly.amount}
          placeholder={localization.monthly_deposit_limit_placeholder}
          iconSrc={
            data?.monthlydepositamount &&
              Number(data.monthlydepositamount) > 0
              ? "/icons/ic-edit.svg"
              : undefined
          }
          disabled={monthly.disabled}
          onButtonClick={() => enableControl("monthlyDepositLimit")}
          onChange={(e) => monthly.setAmount(e.target.value)}
        />
      </div>

      {/* NOTES SECTION */}
      {notes.some((note) => note.note) && (
        <div className="rounded-lg overflow-hidden bg-[#181818] border border-[#333333]">
          {notes.map(
            (note) =>
              note.note && (
                <div
                  key={note.type}
                  className="flex gap-3 p-4 border-b border-[#333333]"
                >
                  <img
                    src="/icons/ic-time.svg"
                    alt="time"
                    className="h-5 w-5 mt-1"
                  />

                  <div className="flex-1">
                    <p className="text-sm text-white leading-relaxed">
                      {note.note}
                    </p>

                    <Button
                      className="mt-1 text-sm font-normal text-[#FFF9A0] hover:underline"
                      onClick={() =>
                        handleUndoRequest(
                          note.type,
                          note.amount ?? null
                        )
                      }
                    >
                      {localization.undo_request}
                    </Button>
                  </div>
                </div>
              )
          )}
        </div>
      )}

      <div className="flex justify-center"><Button
        className="w-full md:w-1/3 py-4 rounded-md text-white bg-primary-gradient uppercase tracking-wider hover:bg-primary-gradient/80 cursor-pointer"
        onClick={handleSubmit}
        disabled={
          Number(daily.amount) <= 0 &&
          Number(weekly.amount) <= 0 &&
          Number(monthly.amount) <= 0
        }
      >
        {localization.submit}
      </Button></div>

    </div>
  );

  return deviceType !== DeviceType.Web ? (
    content
  ) : (
    <Card>
      <CardBody showGradient>
        <div className="w-full max-w-5xl mx-auto">
          {content}
        </div>
      </CardBody>
    </Card>
  );
}